import { useEffect, useMemo, useState } from 'react';
import {
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithCredential,
  signInWithPopup,
  signOut as fbSignOut,
  type User,
} from 'firebase/auth';
import { Capacitor } from '@capacitor/core';
import { auth, getActiveTenantCode } from '../lib/firebase';
import { readSelection, persistSelection, clearBranding, hostForcedTenant } from '../tenant/branding';
import { unregisterPush } from '../native/push';

export interface AgentClaims {
  role?: 'support' | 'tenant_admin' | 'super_admin';
  tenantCode?: string;
  tenantCodes?: string[];
  inboxIds?: string[];
}

function uniq(arr: string[]): string[] {
  return [...new Set(arr.filter(Boolean))];
}

/**
 * Drop every remembered inbox preference (tab / mine-all scope / inbox filter)
 * on sign-out. They are namespaced per tenant+uid by InboxPage, so a stale one
 * can no longer be applied to the next agent — but leaving them behind on a
 * shared machine still means the next person inherits a filtered view they
 * never set, which is how "all my conversations vanished" got reported. Prefix
 * match rather than an imported key list: this file must not depend on a page.
 */
function clearInboxPrefs(): void {
  try {
    const doomed: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith('chatolam:inbox')) doomed.push(k);
    }
    for (const k of doomed) localStorage.removeItem(k);
  } catch {
    // storage unavailable — ignore
  }
}

/** allowedTenants = unique(tenantCodes ∪ (tenantCode ? [tenantCode] : [])). */
function parseAllowedTenants(claims: Record<string, unknown>): string[] {
  const list = Array.isArray(claims.tenantCodes)
    ? (claims.tenantCodes as unknown[]).filter((x): x is string => typeof x === 'string')
    : [];
  const legacy = typeof claims.tenantCode === 'string' ? [claims.tenantCode] : [];
  return uniq([...list, ...legacy]);
}

/** activeTenant = host-forced (if allowed) else persisted (if allowed) else allowedTenants[0]. */
function pickActive(allowed: string[]): string | undefined {
  if (allowed.length === 0) return undefined;
  const forced = hostForcedTenant();
  if (forced && allowed.includes(forced)) return forced;
  const persisted = getActiveTenantCode();
  if (persisted && allowed.includes(persisted)) return persisted;
  return allowed[0];
}

export function useAuth(): {
  user: User | null;
  claims: AgentClaims | null;
  loading: boolean;
  /** Tenant codes the agent may access (from claim; enrich names via my-tenants). */
  availableTenants: string[];
  /** The active tenant code (also overlaid onto claims.tenantCode). */
  activeTenant: string | undefined;
  /** Persist a new active tenant then reload to rebind Firestore. */
  setTenant: (code: string) => void;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
} {
  const [user, setUser] = useState<User | null>(null);
  const [rawClaims, setRawClaims] = useState<Record<string, unknown> | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    return onAuthStateChanged(auth, async (u) => {
      setUser(u);
      if (u) {
        const token = await u.getIdTokenResult();
        setRawClaims(token.claims as Record<string, unknown>);
      } else {
        setRawClaims(null);
      }
      setLoading(false);
    });
  }, []);

  const availableTenants = useMemo(
    () => (rawClaims ? parseAllowedTenants(rawClaims) : []),
    [rawClaims]
  );

  const activeTenant = useMemo(() => pickActive(availableTenants), [availableTenants]);

  // Overlay claims.tenantCode = activeTenant so the ~30 existing consumers that
  // read claims.tenantCode keep working unchanged.
  const claims: AgentClaims | null = useMemo(() => {
    if (!rawClaims) return null;
    return {
      role: rawClaims.role as AgentClaims['role'],
      tenantCode: activeTenant,
      inboxIds: rawClaims.inboxIds as string[] | undefined,
    };
  }, [rawClaims, activeTenant]);

  return {
    user,
    claims,
    loading,
    availableTenants,
    activeTenant,
    setTenant: (code: string) => {
      if (!code) return;
      const existing = readSelection();
      // Preserve enriched name/branding if we're re-selecting the same tenant;
      // otherwise persist a minimal record (name=code) — TenantSwitcher applies
      // full branding before calling this.
      persistSelection({
        code,
        name: existing && existing.code === code ? existing.name : code,
        logoUrl: existing && existing.code === code ? existing.logoUrl : undefined,
        primaryColor: existing && existing.code === code ? existing.primaryColor : undefined,
      });
      window.location.reload();
    },
    signIn: async () => {
      const platform = Capacitor.getPlatform();
      if (platform === 'ios') {
        // @capacitor-firebase/authentication's native pod does not link on iOS
        // (Xcode 26 / CocoaPods drops its static framework). Use
        // @capgo/capacitor-social-login to get a Google idToken natively, then
        // sign into the Firebase web SDK with it.
        const { SocialLogin } = await import('@capgo/capacitor-social-login');
        await SocialLogin.initialize({
          google: {
            iOSClientId: '279873548598-0khqh5euoiqrblddh9248f51cas8jkbf.apps.googleusercontent.com',
            webClientId: '279873548598-log1dpspb84ljljrlnsjbrrvcm9fvn3q.apps.googleusercontent.com',
          },
        });
        const res = await SocialLogin.login({ provider: 'google', options: { scopes: ['email', 'profile'] } });
        const idToken = (res.result as { idToken?: string | null }).idToken;
        if (!idToken) throw new Error('Google sign-in returned no idToken');
        await signInWithCredential(auth, GoogleAuthProvider.credential(idToken));
      } else if (platform === 'android') {
        const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication');
        const result = await FirebaseAuthentication.signInWithGoogle();
        await signInWithCredential(auth, GoogleAuthProvider.credential(result.credential?.idToken));
      } else {
        await signInWithPopup(auth, new GoogleAuthProvider());
      }
    },
    signOut: async () => {
      // Best-effort native cleanup before tearing down auth.
      await unregisterPush().catch(() => {});
      clearBranding();
      clearInboxPrefs();
      const platform = Capacitor.getPlatform();
      try {
        if (platform === 'ios') {
          const { SocialLogin } = await import('@capgo/capacitor-social-login');
          await SocialLogin.logout({ provider: 'google' });
        } else if (platform === 'android') {
          const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication');
          await FirebaseAuthentication.signOut();
        }
      } catch {
        /* ignore */
      }
      await fbSignOut(auth);
    },
  };
}
